/**
 * Which skeleton a pathname loads into (docs/LOADING.md §4.2), pure so the
 * shell's loading chrome (`src/components/loading-chrome.ts`) and its tests
 * can ask without the skeleton components. `src/app/skeletons.tsx` draws the
 * skeletons; this file only names them.
 */

import type { PlainKey } from '@/i18n';

/** The section path segments an item's section screens live at. `sections`
 *  is one added section's screen (`/items/<id>/sections/<key>`, a module or a
 *  flow, 2026-11-02). */
export type SectionSegment =
  | 'activity'
  | 'relationships'
  | 'proposed'
  | 'description'
  | 'files'
  | 'details'
  | 'sections';

/** A section's name by its path segment: the `label` each section page
 *  gives `SectionScreen`, known without data, as a catalog key. An added
 *  section's name is its module's or its flow's, which the path does not
 *  hold, so its skeleton draws the name's shape. */
export const SECTION_NAMES: Record<Exclude<SectionSegment, 'sections'>, PlainKey> = {
  activity: 'shell.section.activity',
  relationships: 'shell.section.relationships',
  proposed: 'shell.section.proposed',
  description: 'shell.section.description',
  files: 'shell.section.files',
  details: 'shell.section.details',
};

/** The section a pathname names (`/items/<id>/<segment>`, or
 *  `/items/<id>/sections/<key>`), or null. */
export function sectionOf(pathname: string): SectionSegment | null {
  const m = /^\/items\/[^/]+\/(activity|relationships|proposed|description|files|details)\/?$/.exec(
    pathname,
  );
  if (m) return m[1] as SectionSegment;
  return /^\/items\/[^/]+\/sections\/[^/]+\/?$/.test(pathname) ? 'sections' : null;
}

export type SkeletonKind = 'item' | 'section' | 'lens' | 'activity' | 'tags' | 'floor';

/** The skeleton a target's pathname loads into, by the route that serves it:
 *  the tap's mount draws this before the router commits, and the route's
 *  `loading.tsx` draws the same one after. A board (`/boards/<id>`) takes the
 *  floor on every layout. */
export function skeletonFor(pathname: string): SkeletonKind {
  if (sectionOf(pathname)) return 'section';
  if (/^\/items\/[^/]+\/?$/.test(pathname)) return 'item';
  if (/^\/(work|views\/[^/]+)\/?$/.test(pathname)) return 'lens';
  if (/^\/activity\/?$/.test(pathname)) return 'activity';
  if (/^\/tags\/?$/.test(pathname)) return 'tags';
  return 'floor';
}

/** Whether a skeleton kind is an item surface (the item or one of its
 *  sections): it opens as the peek in-app, and its top bar is the trail. */
export function isItemSurface(kind: SkeletonKind): kind is 'item' | 'section' {
  return kind === 'item' || kind === 'section';
}
